<div class="mt-4 video-queue-dashboard">
  <div class="row mb-3">
    <div class="col">
      <h2>Video Conversion Queue Management</h2>
      <span class="mt-1 f-600 pointer" (click)="goBack()">
        <span class="icon-arrow-back"></span><span> Zurück zur Liste</span>
      </span>
    </div>
  </div>

  @if (loading) {
    <div class="text-center my-5">
      <div class="spinner-border text-primary" role="status" style="width: 3rem; height: 3rem">
        <span class="visually-hidden">Loading...</span>
      </div>
      <p class="mt-3 text-muted">Loading queue status...</p>
    </div>
  }

  @if (error) {
    <div class="alert alert-danger alert-dismissible fade show" role="alert">
      <i class="fas fa-exclamation-triangle me-2"></i>
      <strong>Error:</strong> {{ error }}
      <button type="button" class="btn-close" aria-label="Close" (click)="error = null"></button>
    </div>
  }

  @if (!loading && queueStatus) {
    <div>
      <!-- Stats Cards -->
      <div class="row mb-4">
        <div class="col-md-3 mb-3">
          <div class="card stat-card stat-card-yellow text-dark h-100">
            <div class="card-body">
              <div class="d-flex justify-content-between align-items-start">
                <div>
                  <h6 class="card-subtitle mb-2">In Queue</h6>
                  <h2 class="card-title mb-0">{{ queueStatus.queue.inQueue }}</h2>
                  <p class="card-text mt-2 mb-0"><small>Waiting for Processing</small></p>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="col-md-3 mb-3">
          <div class="card stat-card stat-card-purple text-white h-100">
            <div class="card-body">
              <div class="d-flex justify-content-between align-items-start">
                <div>
                  <h6 class="card-subtitle mb-2">Processing</h6>
                  <h2 class="card-title mb-0">
                    {{ queueStatus.processing.activeConversions }}/{{ queueStatus.processing.maxConcurrent }}
                  </h2>
                  <p class="card-text mt-2 mb-0">
                    <small>Active Conversions ({{ queueStatus.processing.availableSlots }} slots available)</small>
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="col-md-3 mb-3">
          <div class="card stat-card stat-card-green text-dark h-100">
            <div class="card-body">
              <div class="d-flex justify-content-between align-items-start">
                <div>
                  <h6 class="card-subtitle mb-2">Completed Today</h6>
                  <h2 class="card-title mb-0">{{ queueStatus.stats.completedToday }}</h2>
                  <p class="card-text mt-2 mb-0"><small>Successful Conversions</small></p>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="col-md-3 mb-3">
          <div class="card stat-card stat-card-red text-white h-100">
            <div class="card-body">
              <div class="d-flex justify-content-between align-items-start">
                <div>
                  <h6 class="card-subtitle mb-2">Failed</h6>
                  <h2 class="card-title mb-0">{{ queueStatus.queue.failed }}</h2>
                  <p class="card-text mt-2 mb-0">
                    <small>{{ queueStatus.stats.failedToday }} failed today</small>
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <!-- Queue Check Info -->
      <div class="row mb-3">
        <div class="col-12">
          <div class="alert alert-info d-flex align-items-center">
            <i class="fas fa-info-circle me-2"></i>
            <span
              >Queue is checked every <strong>{{ queueStatus.processing.queueCheckIntervalHours }}</strong> hour(s).
              Uploads will be processed automatically when slots become available.</span
              >
            </div>
          </div>
        </div>
        <!-- Active Conversions Grid -->
        <div class="row">
          <div class="col-12">
            <div class="d-flex justify-content-between align-items-center">
              <h5 class="mb-0">Active Conversions</h5>
              <div class="d-flex align-items-center gap-2">
                <button class="btn btn-primary" (click)="refresh()" [disabled]="loading" title="Refresh">
                  <i class="icon-refresh" [class.fa-spin]="loading"></i> Refresh
                </button>
              </div>
            </div>
            <!-- Column Visibility Selector -->
            @if (showColumnSelector) {
              <div
                class="column-selector-container mb-3 mt-3"
                clickOutside
                (clickOutside)="closeColumnSelector()"
                >
                <div class="modern-filter-container">
                  <!-- Search Box -->
                  <div class="search-container">
                    <input
                      type="text"
                      class="search-input"
                      placeholder="Spalten suchen..."
                      [(ngModel)]="columnSearchText"
                      (input)="onColumnSearchChange()"
                      autocomplete="off"
                      />
                    <i class="search-icon icon-search"></i>
                  </div>
                  <!-- Column Options -->
                  @for (columnField of getFilteredColumns(); track columnField) {
                    <div class="filter-item" data-tooltip-no-anchor>
                      <label class="filter-checkbox">
                        <input
                          type="checkbox"
                          [checked]="columnVisibility[columnField]"
                          (change)="toggleColumnVisibility(columnField)"
                          />
                        <span class="checkmark"></span>
                        <span class="filter-label">
                          {{ getColumnDisplayName(columnField) }}
                        </span>
                      </label>
                    </div>
                  }
                </div>
              </div>
            }
            <div class="card-body">
              @if (activeConversions && activeConversions.totalCount > 0) {
                <div
                  class="ag-theme-ing-plus ag-grid-container position-relative"
                  >
                  <ag-grid-angular
                    class="ag-theme-ing-plus"
                    style="width: 100%; margin-top: 1.25rem"
                    [columnDefs]="columnDefs"
                    [defaultColDef]="defaultColDef"
                    [rowData]="rowData"
                    [suppressPaginationPanel]="suppressPaginationPanel"
                    [rowHeight]="rowHeight"
                    [domLayout]="'autoHeight'"
                    [context]="gridContext"
                    (gridReady)="onGridReady($event)"
                    (sortChanged)="onSortChanged()"
                    (cellClicked)="onCellClicked($event)"
                  ></ag-grid-angular>
                </div>
              } @else {
                <div class="text-center py-5">
                  <i class="fas fa-check-circle fa-3x text-success mb-3"></i>
                  <p class="text-muted mb-0">No active conversions at the moment.</p>
                  <small class="text-muted">Videos will appear here when processing starts.</small>
                </div>
              }
              <!-- Hidden pagination helper -->
              <div class="d-none">
                @for (
                  item of paginationArray
                  | paginate
                  : {
                  itemsPerPage: pageSize,
                  currentPage: currentPage,
                  totalItems: allItems,
                }
                ; track
                item) {
                <div
                  data-tooltip-no-anchor
                ></div>
              }
            </div>
            @if (rowData.length !== 0) {
              <pagination-controls
                (pageChange)="renderPage($event)"
                previousLabel=""
                nextLabel=""
                class="pagination-style text-center mt-3 pt-1"
              ></pagination-controls>
            }
          </div>
        </div>
      </div>
      <!-- Failed Uploads Section -->
      <!--    <div *ngIf="failedUploads && failedUploads.length > 0" class="row mt-4">-->
      <!--      <div class="col-12">-->
      <!--        <div class="card border-danger">-->
      <!--          <div class="card-header bg-danger text-white">-->
      <!--            <h5 class="mb-0"><i class="fas fa-exclamation-triangle me-2"></i>Failed Uploads</h5>-->
    <!--          </div>-->
    <!--          <div class="card-body">-->
    <!--            <div class="alert alert-warning mb-3">-->
    <!--              <i class="fas fa-exclamation-triangle me-2"></i>-->
    <!--              There are <strong>{{ failedUploads.length }}</strong> failed uploads.-->
    <!--              You can retry them to move them back to the queue.-->
  <!--            </div>-->
  <!--            <div class="table-responsive">-->
  <!--              <table class="table table-hover">-->
  <!--                <thead>-->
  <!--                  <tr>-->
  <!--                    <th>Upload Name</th>-->
  <!--                    <th>Tenant</th>-->
  <!--                    <th>Files</th>-->
  <!--                    <th>Upload Date</th>-->
  <!--                    <th>Actions</th>-->
<!--                  </tr>-->
<!--                </thead>-->
<!--                <tbody>-->
<!--                  <tr *ngFor="let upload of failedUploads">-->
<!--                    <td>{{ upload.name }}</td>-->
<!--                    <td>{{ upload.tenant }}</td>-->
<!--                    <td>{{ upload.fileCount }} files</td>-->
<!--                    <td>{{ upload.uploadDate | date:'dd MMM yyyy HH:mm' }}</td>-->
<!--                    <td>-->
<!--                      <button class="btn btn-sm btn-warning"-->
<!--                              (click)="retryFailedUpload(upload.id)"-->
<!--                              [disabled]="retrying[upload.id]">-->
<!--                        <span *ngIf="retrying[upload.id]" class="spinner-border spinner-border-sm me-1" role="status"></span>-->
<!--                        <i *ngIf="!retrying[upload.id]" class="fas fa-redo me-1"></i>-->
<!--                        {{ retrying[upload.id] ? 'Retrying...' : 'Retry' }}-->
<!--                      </button>-->
<!--                    </td>-->
<!--                  </tr>-->
<!--                </tbody>-->
<!--              </table>-->
<!--            </div>-->
<!--          </div>-->
<!--        </div>-->
<!--      </div>-->
<!--    </div>-->
</div>
}
</div>
